/** The steps of a button (docs/plans/buttons-and-place-lists.md): one thing each, in a workspace of its own. */
import type { TFunction } from "i18next";

export type Action =
  | "done"
  | "undone"
  | "doneToggle"
  | "start"
  | "stop"
  | "timerToggle"
  | "timerStartPause"
  | "capture"
  | "addTask"
  | "habitAdd"
  | "habitSubtract"
  | "habitYes"
  | "habitNo"
  | "habitToggle"
  | "pomodoroStart"
  | "pomodoroStop"
  | "pomodoroToggle"
  | "pomodoroStartPause";
export const HABIT_ACTIONS: Action[] = ["habitAdd", "habitSubtract", "habitYes", "habitNo", "habitToggle"];
export const MAX_STEPS = 10;

/** A step as the server shows it, and as the form keeps it before saving. */
export interface StepInfo {
  workspaceId: string;
  /** null when you can no longer change things there */
  workspaceName: string | null;
  action: Action;
  taskId: string | null;
  taskTitle: string | null;
  /** acts on the selected task: the one open in a task panel, else the timer's */
  selected?: boolean;
  counterId: string | null;
  /** null when the habit was archived or deleted */
  counterTitle: string | null;
  unit: string;
  amount: number | null;
  text: string;
}

export interface ButtonInfo {
  id: number;
  workspaceId: string;
  steps: StepInfo[];
  label: string;
  url: string;
  lastUsedAt: string | null;
}

/** What the server is sent for a step. */
export function stepBody(s: StepInfo) {
  return {
    workspaceId: s.workspaceId,
    action: s.action,
    taskId: s.selected ? null : s.taskId,
    selected: !!s.selected,
    counterId: s.counterId,
    amount: s.amount,
    text: s.text,
  };
}

/** One step in words; with the workspace's name when it is not the button's own one. */
export function describeStep(t: TFunction, fmt: (n: number) => string, s: StepInfo, home: string): string {
  const does =
    s.action === "capture" || s.action === "addTask"
      ? t(`buttons.does.${s.action}`, { text: s.text })
      : HABIT_ACTIONS.includes(s.action)
        ? t(`buttons.does.${s.action}`, {
            title: s.counterTitle ?? t("buttons.habitGone"),
            n: fmt(s.amount ?? 1),
            unit: s.unit ? ` ${s.unit}` : "",
          })
        : s.action.startsWith("pomodoro")
          ? t(`buttons.does.${s.action}`)
          : t(`buttons.does.${s.action}`, {
              title: s.selected ? t("buttons.selectedTask") : (s.taskTitle ?? t("buttons.taskGone")),
            });
  if (s.workspaceId === home) return does;
  return t("buttons.inWorkspace", { does, name: s.workspaceName ?? t("buttons.workspaceGone") });
}

/** A button in words: its steps, one after the other. */
export function describeButton(t: TFunction, fmt: (n: number) => string, b: ButtonInfo): string {
  return b.steps.map((s) => describeStep(t, fmt, s, b.workspaceId)).join(" · ");
}
